"use client";

import { useEffect, useState, type HTMLAttributes, type ReactNode } from "react";
import { motion, useReducedMotion, type HTMLMotionProps } from "framer-motion";
import { fadeUp } from "@/lib/motion";

type GradientBorderCardProps = {
  children: ReactNode;
  className?: string;
  innerClassName?: string;
} & HTMLMotionProps<"div">;

export function GradientBorderCard({ children, className, innerClassName, ...rest }: GradientBorderCardProps) {
  const shouldReduceMotion = useReducedMotion();
  const [hasMounted, setHasMounted] = useState(false);
  const baseClassName = `group rounded-[28px] bg-gradient-gold/80 p-[1px] transition hover:-translate-y-[3px] hover:shadow-[0_0_25px_rgba(255,200,150,0.1)]${
    className ? ` ${className}` : ""
  }`;
  const innerBaseClassName = `rounded-[26px] border border-border-subtle bg-[var(--public-gradient-card-bg)] p-6 md:p-7 backdrop-blur-md shadow-soft text-foreground dark:border-white/10${
    innerClassName ? ` ${innerClassName}` : ""
  }`;
  const canObserveViewport = typeof window !== "undefined" && "IntersectionObserver" in window;
  const shouldAnimate = hasMounted && !shouldReduceMotion && canObserveViewport;
  const staticProps = rest as HTMLAttributes<HTMLDivElement>;

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => setHasMounted(true));
    return () => window.cancelAnimationFrame(frame);
  }, []);

  if (!shouldAnimate) {
    return (
      <div className={baseClassName} {...staticProps}>
        <div className={innerBaseClassName}>
          {children}
        </div>
      </div>
    );
  }

  return (
    <motion.div
      variants={fadeUp}
      initial="initial"
      whileInView="animate"
      viewport={{ once: true, amount: 0.25 }}
      className={baseClassName}
      {...rest}
    >
      <div className={innerBaseClassName}>
        {children}
      </div>
    </motion.div>
  );
}
